import JsSIP from '@wui/jssip'

enum CallStatusState {
  UNKNOWN = 'unknown',
  TERMINATED = 'terminated',
  EARLY = 'early',
  CONFIRMED = 'confirmed',
  HOLD = 'hold',
}

interface NotifyBody {
  internalNumber: string
  registered?: boolean
  remote?: CallStatusState
  local?: CallStatusState
  callId?: string | null
  callFromNumber?: string
  callFromName?: string
  direction: 'recipient' | 'initiator'
}

const domParser = new DOMParser()

const readDialogDirection = (
  element: Element | undefined,
  fallback: NotifyBody['direction'],
): NotifyBody['direction'] => {
  const value = element?.getAttribute('direction')
  if (value === 'recipient' || value === 'initiator') {
    return value
  }
  return fallback
}

export function notifyBodyParser (body: string): NotifyBody {
  const DOM = domParser.parseFromString(body.replace('application/dialog-info+xml', ''), 'application/xml')
  const PRESENSE: Element | undefined = DOM.getElementsByTagName('presence')[0]
  const scope: Element = PRESENSE ?? DOM.documentElement
  const ALERTING: Element | undefined = scope.getElementsByTagName('ce:alerting')[0]
  const ACTIVITIES: Element | undefined = scope.getElementsByTagName('e:activities')[0]
  const ON_THE_PHONE: Element | undefined = ACTIVITIES?.getElementsByTagName('e:on-the-phone')[0]
  const TERMINATED: Element | undefined = ACTIVITIES?.getElementsByTagName('ce:terminated')[0]
  const STATUS: Element | undefined = scope.getElementsByTagName('status')[0]

  const imLogin = PRESENSE?.getAttribute('entity')
  const alertingNumberURIString = ALERTING?.getAttribute('identity')
  const alertingName = ALERTING?.getAttribute('display')
  const alertingCallId = ALERTING?.getAttribute('call-id')
  const confirmedCallId = ON_THE_PHONE?.getAttribute('call-id')
  const terminatedCallId = TERMINATED?.getAttribute('call-id')
  const direction = ALERTING
    ? readDialogDirection(ALERTING, 'recipient')
    : readDialogDirection(ON_THE_PHONE, 'initiator')
  const registered = STATUS?.textContent === 'open'
  const onThePhoneNumberURIString = ON_THE_PHONE?.getAttribute('identity')
  const onThePhoneName = ON_THE_PHONE?.getAttribute('display')

  if (!imLogin) throw new Error('no imLogin at notify')

  const internalNumber = JsSIP.URI.parse(imLogin)?.user

  if (!internalNumber) throw new Error('no internalNumber at notify')

  let remote: CallStatusState = CallStatusState.UNKNOWN
  if (alertingCallId) {
    remote = CallStatusState.EARLY
  } else if (terminatedCallId && (!confirmedCallId || terminatedCallId === confirmedCallId)) {
    remote = CallStatusState.TERMINATED
  } else if (confirmedCallId) {
    remote = CallStatusState.CONFIRMED
  } else if (terminatedCallId) {
    remote = CallStatusState.TERMINATED
  }

  const callId = alertingCallId
    ?? (remote === CallStatusState.TERMINATED ? terminatedCallId : undefined)
    ?? confirmedCallId
    ?? terminatedCallId
    ?? undefined

  let callFromNumber: string | undefined
  let callFromName: string | undefined

  if (onThePhoneNumberURIString) {
    callFromNumber = JsSIP.URI.parse(onThePhoneNumberURIString)?.user
    callFromName = onThePhoneName!
  }

  if (alertingNumberURIString) {
    callFromNumber = JsSIP.URI.parse(alertingNumberURIString)?.user
    callFromName = alertingName!
  }

  return {
    callId: callId!,
    callFromNumber: callFromNumber!,
    direction,
    internalNumber,
    callFromName: callFromName!,
    registered,
    remote,
  }
}
